/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  createContext,
  useCallback,
  useEffect,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  GestaoDeCronicosStepsEnum,
  IResponseQuestionnaire,
  IResultIMC,
  ITermResp,
} from "../../../../interfaces/enum/GestaoDeCronicosEnum";
import {
  getBoasVindas,
  getBoasVindasDetalhes,
  getHorariosDisponiveisPeriodo,
  getRespostasQuestionario,
  getTermoAceiteCronicos,
  getTermoRespCronicos,
  getTermoRetornoAceiteCronicos,
  postCronicosAgendar,
} from "../../../../services/SaudeBemEstar/GestaoDeCronicosService";
import { Horarios } from "../../../../interfaces/entities/Horarios";
import moment from "moment";

interface CaptacaoContextType {
  loading: boolean;
  loadingQuest: boolean;
  texts: string[];
  images: string[];
  open: boolean;
  setOpen: (open: boolean) => void;
  termResp?: ITermResp;
  termAccept?: any;
  responseQuestionnaire?: IResponseQuestionnaire;
  currentStep: GestaoDeCronicosStepsEnum;
  dateSelected: boolean;
  respIMC?: IResultIMC;
  openErrorModal: boolean;
  datesAvailable: Horarios[];
  hoursOptions: Horarios[];
  hourSelected: string;
  showConfirmacaoModal: boolean;
  setShowConfirmacaoModal: Dispatch<SetStateAction<boolean>>
  handleDate: (date: Date) => void;
  handleMonth: (date: Date) => void;
  setHourSelected: (hour: string) => void;
  closeErrorModal: () => void;
  getDetails: () => void;
  quizAnswers: () => void;
  getTermRespAccept: () => void;
  handleChangeStep: (
    newStep: GestaoDeCronicosStepsEnum,
    data?: { responseIMC?: IResultIMC }
  ) => void;
  handleBackButtonClick: () => void;
  handleTermsOfUse: (accept: boolean) => void;
  chronicScheduling: (userData: any) => void;
}

const CaptacaoContext = createContext<
  CaptacaoContextType | undefined
>(undefined);

export const CaptacaoProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingQuest, setLoadingQuest] = useState<boolean>(true);
  const [texts, setTexts] = useState<string[]>([]);
  const [images, setImages] = useState<string[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [openErrorModal, setOpenErrorModal] = useState<boolean>(false);
  const [showConfirmacaoModal, setShowConfirmacaoModal] =
    useState<boolean>(false);
  const [termResp, setTermResp] = useState<ITermResp>();
  const [termAccept, setTermAccept] = useState<any>();
  const [datesAvailable, setDatesAvailable] = useState<Horarios[]>([]);
  const [hoursOptions, setHoursOptions] = useState<Horarios[]>([]);
  const [hourSelected, setHourSelected] = useState<string>("");
  const [dateSelected, setDateSelected] = useState<boolean>(false);
  const [responseQuestionnaire, setResponseQuestionnaire] =
    useState<IResponseQuestionnaire>();
  const [currentStep, setCurrentStep] = useState<GestaoDeCronicosStepsEnum>(
    GestaoDeCronicosStepsEnum.loading
  );
  const [respIMC, setRespIMC] = useState<IResultIMC>();
  const [stepHistory, setStepHistory] = useState<GestaoDeCronicosStepsEnum[]>(
    []
  );
  const { idComunic } = useParams<{ step: string; idComunic: string }>();

  useEffect(() => {
    setLoading(true);
    getBoasVindas()
      .then((response) => {
        if (response !== null) {
          setTexts(response.textos);
          setImages(response.imagens);
          handleChangeStep(GestaoDeCronicosStepsEnum.inicio);
          return;
        }
        setOpenErrorModal(true);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    handleMonth(new Date());
  }, []);

  const handleMonth = useCallback((date: Date) => {
    const month = date.getMonth();
    const start = moment().month(month).startOf("month").format("DD/MM/YYYY");
    const end = moment().month(month).endOf("month").format("DD/MM/YYYY");

    getHorariosDisponiveisPeriodo(start, end).then((res) => {
      setDatesAvailable(res);
    });
  }, []);

  const handleDate = useCallback(
    (date: Date) => {
      const dateString = moment(date).format("DD/MM/YYYY");
      const options = datesAvailable.filter((d) => d.data === dateString);
      setDateSelected(true);
      setHoursOptions(options);
      setHourSelected("");
    },
    [datesAvailable]
  );

  const closeErrorModal = () => setOpenErrorModal(false);

  const getDetails = useCallback(() => {
    setLoading(true);
    getBoasVindasDetalhes()
      .then((response) => {
        if (response !== null) {
          setTexts(response.textos);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const quizAnswers = useCallback(() => {
    if (idComunic) {
      setLoadingQuest(true);
      getRespostasQuestionario(idComunic)
        .then((response) => {
          if (response !== null) {
            setResponseQuestionnaire(response);
          }
        })
        .finally(() => {
          setLoadingQuest(false);
        });
    }
  }, [idComunic]);

  const chronicScheduling = useCallback(
    (userData: any) => {
      setLoading(true);
      const selectedData = datesAvailable.find(
        (x) => `${x.data} ${x.hora}` === hourSelected
      );

      const values = {
        idInstal: selectedData?.idInstal || "",
        idPrestador: selectedData?.idPrestador || "",
        data: `${selectedData?.data} ${selectedData?.hora}` || "",
        hora: selectedData?.hora || "",
        diaSemana: selectedData?.diaSemana || "",
        intervaloMinuto: selectedData?.intervaloMinuto || "",
        indPermiteRet: selectedData?.indPermiteRet || "",
        email: userData?.email,
        tel: userData?.tel,
        cel: userData?.cel,
        idComunic: idComunic,
        tipoAtend: "",
        idTermoResponsabilidade: termAccept?.idTermoResponsabilidade.toString(),
      };

      postCronicosAgendar(values)
        .then((response) => {
          if (response === null) {
            setOpenErrorModal(true);
            return;
          }
          setShowConfirmacaoModal(true);
        })
        .finally(() => setLoading(false));
    },
    [datesAvailable, hourSelected]
  );

  const termsOfAccept = useCallback(() => {
    setLoading(true);
    getTermoRespCronicos()
      .then((response) => {
        if (response !== null) {
          setTermResp(response);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const getTermRespAccept = useCallback(() => {
    setLoading(true);
    getTermoRetornoAceiteCronicos()
      .then((response) => {
        if (response !== null) {
          setTermAccept(response);
          if (response.indAceite === "N") {
            termsOfAccept();
            setOpen(true);
            return;
          }
          quizAnswers();
          handleChangeStep(GestaoDeCronicosStepsEnum.questionarioIMC);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (currentStep === GestaoDeCronicosStepsEnum.loading) {
      setCurrentStep(GestaoDeCronicosStepsEnum.inicio);
    }
  }, [currentStep]);

  const navigateToStep = (step: GestaoDeCronicosStepsEnum) => {
    if (step === GestaoDeCronicosStepsEnum.inicio) {
      navigate(`/saudeBemEstar/gestaoDeCronicos/captacao/inicio/${idComunic}`);
      return;
    }
    navigate(`/saudeBemEstar/gestaoDeCronicos/${step}/${idComunic}`);
  };

  const handleChangeStep = (
    newStep: GestaoDeCronicosStepsEnum,
    data: { responseIMC?: IResultIMC } = {}
  ) => {
    if (newStep !== currentStep) {
      setStepHistory((prev) => [...prev, currentStep]);
      setCurrentStep(newStep);
      if (data.responseIMC) setRespIMC(data.responseIMC);
      navigateToStep(newStep);
    }
  };

  const handleBackButtonClick = () => {
    const newStepHistory = [...stepHistory];

    const previousStep = newStepHistory.pop();

    if (previousStep) {
      setCurrentStep(previousStep);
      setStepHistory(newStepHistory);

      navigate(-1);
    }
  };

  const handleTermsOfUse = (accept: boolean) => {
    setLoading(true);
    if (accept) {
      setOpen(false);
      getTermoAceiteCronicos(
        parseInt(termAccept?.idTermoResponsabilidade),
        true
      ).finally(() => {
        setLoading(false);
        quizAnswers();
        handleChangeStep(GestaoDeCronicosStepsEnum.questionarioIMC);
      });
      return;
    }
    setLoading(false);
    setOpen(false);
  };

  return (
    <CaptacaoContext.Provider
      value={{
        loading,
        loadingQuest,
        texts,
        images,
        open,
        setOpen,
        termResp,
        termAccept,
        responseQuestionnaire,
        currentStep,
        respIMC,
        openErrorModal,
        dateSelected,
        datesAvailable,
        hoursOptions,
        hourSelected,
        showConfirmacaoModal,
        setShowConfirmacaoModal,
        handleDate,
        handleMonth,
        setHourSelected,
        closeErrorModal,
        getDetails,
        quizAnswers,
        getTermRespAccept,
        handleChangeStep,
        handleBackButtonClick,
        handleTermsOfUse,
        chronicScheduling,
      }}
    >
      {children}
    </CaptacaoContext.Provider>
  );
};

export const useGestaoDeCronicosCaptacao = () => {
  const context = React.useContext(CaptacaoContext);
  if (context === undefined) {
    throw new Error(
      "useGestaoDeCronicosCaptacao must be used within a CaptacaoProvider"
    );
  }
  return context;
};
